import React, {
  ReactNode,
  RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ScrollContainer,
  ShadowContainer,
  ShadowLeft,
  ShadowRight,
} from "./HorizontalScrollStyles";

export default function HorizontalScroll({
  children,
  childrenRef,
}: {
  children: ReactNode;
  childrenRef: RefObject<HTMLDivElement | null>;
}) {
  const topWrapperRef = useRef<HTMLDivElement | null>(null);
  const bottomWrapperRef = useRef<HTMLDivElement | null>(null);
  const [childrenWidth, setChildrenWidth] = useState(0);
  const [childrenHeight, setChildrenHeight] = useState(0);
  const [windowHeight, setWindowHeight] = useState(0);

  useEffect(() => {
    const topWrapperRefCurrent = topWrapperRef.current;
    const bottomWrapperRefCurrent = bottomWrapperRef.current;

    const topListener = () => {
      if (topWrapperRefCurrent !== null && bottomWrapperRefCurrent !== null) {
        bottomWrapperRefCurrent.scrollLeft = topWrapperRefCurrent.scrollLeft;
      }
    };
    const bottomListener = () => {
      if (topWrapperRefCurrent !== null && bottomWrapperRefCurrent !== null) {
        topWrapperRefCurrent.scrollLeft = bottomWrapperRefCurrent.scrollLeft;
      }
    };

    if (topWrapperRefCurrent !== null) {
      topWrapperRefCurrent.addEventListener("scroll", topListener);
    }
    if (bottomWrapperRef.current !== null) {
      bottomWrapperRef.current.addEventListener("scroll", bottomListener);
    }
    return () => {
      if (topWrapperRefCurrent !== null) {
        topWrapperRefCurrent.removeEventListener("scroll", topListener);
      }
      if (bottomWrapperRefCurrent !== null) {
        bottomWrapperRefCurrent.removeEventListener("scroll", bottomListener);
      }
    };
  }, [windowHeight, childrenHeight]);

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    setWindowHeight(window.innerHeight);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setChildrenWidth(entry.contentRect.width);
        setChildrenHeight(entry.contentRect.height);
      }
    });

    const currentChildrenRef = childrenRef.current;
    if (currentChildrenRef) {
      resizeObserver.observe(currentChildrenRef);
    }

    return () => {
      if (currentChildrenRef) {
        resizeObserver.unobserve(currentChildrenRef);
      }
    };
  }, [childrenRef]);

  const shadowContainerRef = useRef<HTMLDivElement | null>(null);
  const shadowLeftRef = useRef<HTMLDivElement | null>(null);
  const shadowRightRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const currentContentRef = bottomWrapperRef.current;
    if (currentContentRef !== null) {
      const onScroll = () => {
        if (shadowContainerRef.current !== null) {
          const contentScrollHeight =
            currentContentRef.scrollWidth -
            shadowContainerRef.current.offsetWidth;
          const currentScroll =
            currentContentRef.scrollLeft / contentScrollHeight;
          if (contentScrollHeight == 0) {
            if (shadowLeftRef.current !== null) {
              shadowLeftRef.current.style.opacity = "0";
            }
            if (shadowRightRef.current !== null) {
              shadowRightRef.current.style.opacity = "0";
            }
          } else {
            if (shadowLeftRef.current !== null) {
              shadowLeftRef.current.style.opacity = currentScroll.toString();
            }
            if (shadowRightRef.current !== null) {
              shadowRightRef.current.style.opacity = (
                1 - currentScroll
              ).toString();
            }
          }
        }
      };
      onScroll();
      currentContentRef?.addEventListener("scroll", onScroll);
      return () => {
        currentContentRef?.removeEventListener("scroll", onScroll);
      };
    }
  });

  return (
    <ShadowContainer ref={shadowContainerRef}>
      {childrenHeight > windowHeight && (
        <ScrollContainer height={"1.1rem"} ref={topWrapperRef}>
          <div style={{ width: childrenWidth }} />
        </ScrollContainer>
      )}
      <ScrollContainer ref={bottomWrapperRef}>
        <ShadowLeft ref={shadowLeftRef} />
        <ShadowRight ref={shadowRightRef} />
        {children}
      </ScrollContainer>
    </ShadowContainer>
  );
}
